<script lang="ts">
  import { api } from '../../lib/api/client';
  import type { TimelineCalendar, TimelineMeasure } from '../../lib/api/types';
  import { t } from '../../lib/i18n/index.ts';
  import { distance, span } from './format';
  import YearDays from './YearDays.svelte';

  // YearTab is the timeline's year at a glance (spec 005 FR-048):
  // each day shaded by distance traveled, hours away from the home city, or
  // places visited, opening its day, with the year's streaks of days away
  // and of days with a new place.
  let { onDay }: { onDay: (date: string) => void } = $props();

  const MEASURES: { id: TimelineMeasure; name: string; color: string }[] = [
    { id: 'distance', name: t('timelineDistance'), color: 'var(--theme-blue)' },
    { id: 'away', name: t('timelineHoursAway'), color: 'var(--theme-peach)' },
    { id: 'places', name: t('timelinePlaces'), color: 'var(--theme-green)' },
  ];
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const thisYear = new Date().getFullYear();
  let measure = $state<TimelineMeasure>('distance');
  let year = $state(thisYear);
  let cal = $state<TimelineCalendar | null>(null);
  let error = $state('');

  $effect(() => {
    const m = measure, y = year;
    api.get<TimelineCalendar>(`/me/timeline/calendar?year=${y}&measure=${m}&tz=${encodeURIComponent(tz)}`)
      .then((got) => { if (got.year === year && got.measure === measure) { cal = got; error = ''; } })
      .catch((e) => (error = e.message));
  });

  const current = $derived(cal && cal.year === year && cal.measure === measure ? cal : null);
  const values = $derived(new Map((current?.days ?? []).map((d) => [d.date, d.value])));
  const color = $derived(MEASURES.find((m) => m.id === measure)!.color);

  function text(v: number | undefined): string {
    if (v == null) return t('timelineNothingRecorded');
    switch (measure) {
      case 'distance': return v > 0 ? t('timelineTraveled', { distance: distance(v) }) : t('timelineNoTravel');
      case 'away': return v > 0 ? t('timelineAwayFromHome', { time: span(v) }) : t('timelineAtHome');
      default: return t('timelinePlaceCount', { n: Math.round(v) });
    }
  }

  const days = (n: number) => t('timelineDayCount', { n });
</script>

<div class="flex-1 overflow-y-auto p-4 sm:p-6">
  <div class="max-w-4xl mx-auto space-y-4">
    <section class="card p-4 space-y-3" aria-labelledby="year-heading">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <h2 id="year-heading" class="font-semibold">{t('timelineYear')}</h2>
        <div class="flex items-center gap-1 text-sm">
          <button class="btn btn-secondary btn-sm" aria-label={t('timelineYearBefore')} onclick={() => year--}><i aria-hidden="true" class="las la-chevron-left"></i></button>
          <span class="px-1 font-medium" aria-live="polite">{year}</span>
          <button class="btn btn-secondary btn-sm" aria-label={t('timelineYearAfter')} disabled={year >= thisYear} onclick={() => year++}><i aria-hidden="true" class="las la-chevron-right"></i></button>
        </div>
      </div>
      <div class="flex gap-1" role="group" aria-label={t('timelineMeasure')}>
        {#each MEASURES as m (m.id)}
          <button class="btn btn-sm {measure === m.id ? 'btn-primary' : 'btn-secondary'}" aria-pressed={measure === m.id} onclick={() => (measure = m.id)}>{m.name}</button>
        {/each}
      </div>
      {#if error}
        <p class="text-sm" style="color: var(--theme-error);">{error}</p>
      {/if}
      <YearDays {year} {values} max={current?.max ?? 0} {color} {text} name={t('timelineDaysOf', { year: String(year) })} onOpen={onDay} />
    </section>

    {#if current}
      <section class="card p-4" aria-labelledby="streaks-heading">
        <h2 id="streaks-heading" class="font-semibold mb-3">{t('timelineStreaksIn', { year: String(year) })}</h2>
        <dl class="grid grid-cols-2 gap-4">
          <div>
            <dt class="text-xs text-text-muted">{t('timelineAwayStreak')}</dt>
            <dd class="text-xl font-semibold tabular-nums">{days(current.streaks.away.longest)}</dd>
            <dd class="text-xs text-text-muted">{current.streaks.away.current ? t('timelineStreakNow', { days: current.streaks.away.current }) : t('timelineNoneNow')}</dd>
          </div>
          <div>
            <dt class="text-xs text-text-muted">{t('timelineNewPlaceStreak')}</dt>
            <dd class="text-xl font-semibold tabular-nums">{days(current.streaks.new_place.longest)}</dd>
            <dd class="text-xs text-text-muted">{current.streaks.new_place.current ? t('timelineStreakNow', { days: current.streaks.new_place.current }) : t('timelineNoneNow')}</dd>
          </div>
        </dl>
        <p class="text-xs text-text-muted mt-3">{t('timelineDayAway')}</p>
      </section>
    {/if}
  </div>
</div>
